import { useMemo, useState, useTransition } from 'react';

export const PAGINATION_KEY_SUFFIX = '-pagination';

type Pagination<T extends string> = {
  order?: 'asc' | 'desc';
  orderBy?: T;
  page: number;
  rowsPerPage: number;
};

type ReturnTypeWithOrder<T extends string> = {
  orderBy: T;
  order: 'asc' | 'desc';
  page: number;
  rowsPerPage: number;
  handleRequestSort: (_: unknown, property: string) => void;
  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: {
    target: { value: string | number };
  }) => void;
  genericSorter: (a: Record<T, unknown>, b: Record<T, unknown>) => number;
  toFirstPage: () => void;
  pageBack: () => void;
  pageForward: () => void;
  properties: {
    orderBy: T;
    order: 'asc' | 'desc';
    page: number;
    rowsPerPage: number;
  };
  isPaginationChanging: boolean;
  handleChangeSort: (orderBy: T, order: 'asc' | 'desc') => void;
};

type ReturnTypeWithoutOrder<T extends string> = {
  orderBy?: T;
  order?: 'asc' | 'desc';
  page: number;
  rowsPerPage: number;
  handleRequestSort: (_: unknown, property: string) => void;
  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: {
    target: { value: string | number };
  }) => void;
  genericSorter: (a: Record<T, unknown>, b: Record<T, unknown>) => number;
  toFirstPage: () => void;
  pageBack: () => void;
  pageForward: () => void;
  properties: {
    orderBy?: T;
    order?: 'asc' | 'desc';
    page: number;
    rowsPerPage: number;
  };
  isPaginationChanging: boolean;
  handleChangeSort: (orderBy: T, order: 'asc' | 'desc') => void;
};

type Props<T extends string> = {
  id?: string;
  order?: 'asc' | 'desc';
  orderBy?: T;
  page?: number;
  pageSize?: number;
};

export function usePagination<T extends string>(
  defaults: Props<T> & { order: 'asc' | 'desc'; orderBy: T },
  orderByOptions?: readonly [T, ...Array<T>],
): ReturnTypeWithOrder<T>;

export function usePagination<T extends string>(
  defaults?: Props<T>,
  orderByOptions?: readonly [T, ...Array<T>],
): ReturnTypeWithoutOrder<T>;

export function usePagination<T extends string>(
  defaults: Props<T> = {},
  orderByOptions?: readonly [T, ...Array<T>],
): ReturnTypeWithOrder<T> | ReturnTypeWithoutOrder<T> {
  const [isPaginationChanging, startTransition] = useTransition();

  const [pagination, setPagination] = useState<Pagination<T>>(() => {
    const savedPagination = defaults.id
      ? (JSON.parse(
          localStorage.getItem(`${defaults.id}${PAGINATION_KEY_SUFFIX}`) ??
            '{}',
        ) as Pagination<T>)
      : {
          page: 1,
          rowsPerPage: 10,
        };

    return {
      order: savedPagination.order ?? defaults.order,
      orderBy: savedPagination.orderBy ?? defaults.orderBy,
      page: savedPagination.page ?? defaults.page ?? 1,
      rowsPerPage: savedPagination.rowsPerPage ?? defaults.pageSize ?? 10,
    };
  });

  if (
    orderByOptions &&
    defaults.orderBy &&
    !orderByOptions.includes(defaults.orderBy)
  ) {
    throw new Error(`Invalid default orderBy option: ${defaults.orderBy}`);
  }

  return useMemo(() => {
    const handleChangePagination = (
      changedPagination: Partial<Pagination<T>>,
    ) => {
      const updatedPagination = {
        ...pagination,
        ...changedPagination,
      };
      startTransition(() => {
        setPagination(updatedPagination);
      });

      if (defaults.id) {
        localStorage.setItem(
          `${defaults.id}${PAGINATION_KEY_SUFFIX}`,
          JSON.stringify(updatedPagination),
        );
      }
    };

    const handleRequestSort = (_: unknown, property: string) => {
      const isAsc =
        pagination.orderBy === property && pagination.order === 'asc';

      if (orderByOptions && !orderByOptions.includes(property as T)) {
        throw new Error(`Invalid orderBy option: ${property}`);
      }

      startTransition(() => {
        handleChangePagination({
          orderBy: property as T,
          order: isAsc ? 'desc' : 'asc',
          page: 1,
        });
      });
    };

    const handleChangeSort = (orderBy: T, order: 'asc' | 'desc') => {
      startTransition(() => {
        handleChangePagination({ orderBy, order, page: 1 });
      });
    };

    const changePageByStep = (step: number) => {
      if (pagination.page + step > 0) {
        startTransition(() => {
          handleChangePagination({ page: pagination.page + step });
        });
      }
    };

    const handleChangePage = (_: unknown, newPage: number) => {
      if (newPage >= 0) {
        startTransition(() => {
          handleChangePagination({ page: newPage + 1 });
        });
      }
    };

    const handleChangeRowsPerPage = (event: {
      target: { value: string | number };
    }) => {
      const value =
        typeof event.target.value === 'string'
          ? parseInt(event.target.value, 10)
          : event.target.value;

      startTransition(() => {
        handleChangePagination({
          rowsPerPage: value,
          page: 1,
        });
      });
    };

    const genericSorter = (a: Record<T, unknown>, b: Record<T, unknown>) => {
      const { order, orderBy } = pagination;

      if (!orderBy || !order) return 0;
      if (a[orderBy] < b[orderBy]) {
        return order === 'asc' ? -1 : 1;
      }
      if (a[orderBy] > b[orderBy]) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    };

    return {
      orderBy: pagination.orderBy,
      order: pagination.order,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      handleRequestSort,
      handleChangePage,
      handleChangeRowsPerPage,
      genericSorter,
      toFirstPage: () => {
        handleChangePagination({ page: 1 });
      },
      pageBack: () => {
        changePageByStep(-1);
      },
      pageForward: () => {
        changePageByStep(1);
      },
      properties: pagination,
      isPaginationChanging,
      handleChangeSort,
    };
  }, [defaults.id, orderByOptions, pagination, isPaginationChanging]);
}
